/**
 * Communication Audit Log Tab
 *
 * Renders the supplier-onboarding audit feed in a When / Company / User /
 * Description / Attachments table — same layout as Delivery Appeal History
 * (CR-4611) and the Acknowledgment History panel (CR-4661) so the audit
 * surface looks the same wherever it appears in the platform.
 *
 * CR-4477 round 2: switched from per-workflow client-side merge (which
 * silently rendered empty because the workflow LIST endpoint omits
 * activity_logs) to the dedicated /onboarding/workflows/supplier-audit-log/
 * endpoint that aggregates across every workflow the caller can see for the
 * supplier. Backend now also writes log rows from invitation acceptance,
 * questionnaire submit/approve/reject/more-info, and a one-shot data
 * migration backfilled historical workflows from their lifecycle
 * timestamps so the timeline renders for legacy suppliers too.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, History } from 'lucide-react';
import { onboardingAPI, type OnboardingActivityLog } from '../../lib/api';
import { SupplierProfile } from '../../lib/api';
import { usePreferences } from '../../contexts/PreferencesContext';
import { ChatPanel } from './ChatPanel';

interface ActivityTabProps {
  supplier: SupplierProfile;
}

// Pretty action labels for the legacy snake_case action codes the backend
// stores. Anything unmapped falls through with title-case formatting.
// CR-4477 round 3: Jacqueline asked for the labels to use supplier-onboarding
// terminology instead of "Workflow" — the latter is an internal model name
// that confuses customer-portal users reading the audit timeline.
const ACTION_LABELS: Record<string, string> = {
  workflow_started: 'Onboarding started',
  workflow_submitted: 'Submitted for review',
  workflow_approved: 'Onboarding approved',
  workflow_declined: 'Onboarding declined',
  workflow_reapplied: 'Re-application submitted',
  supplier_registered: 'Supplier registered',
  approved: 'Supplier approved',
  declined: 'Supplier declined',
  questionnaire_submitted: 'Questionnaire submitted',
  questionnaire_approved: 'Questionnaire approved',
  questionnaire_rejected: 'Questionnaire rejected',
  questionnaire_auto_approved: 'Questionnaire auto-approved',
  questionnaire_more_info_requested: 'More information requested',
  all_steps_completed: 'All required steps completed',
};

// Strip the "workflow" wording from legacy backfilled description strings so
// pre-rework history rows render with onboarding terminology too. The 0009
// data migration rewrites stored descriptions, but this guard keeps the UI
// consistent for any rows the migration can't reach (test fixtures, replicas).
function humaniseDescription(description: string): string {
  if (!description) return description;
  return description
    .replace(/\bworkflow approved\b/gi, 'onboarding approved')
    .replace(/\bworkflow declined\b/gi, 'onboarding declined')
    .replace(/\bworkflow submitted for review\b/gi, 'onboarding submitted for review')
    .replace(/\bWorkflow approved by\b/g, 'Onboarding approved by')
    .replace(/\bWorkflow declined\b/g, 'Onboarding declined');
}

function formatAction(action: string): string {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ActivityTab({ supplier }: ActivityTabProps) {
  const { formatDate } = usePreferences();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<OnboardingActivityLog[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditData = useCallback(async () => {
    if (!supplier?.company) return;
    try {
      setLoading(true);
      setError(null);
      const res = await onboardingAPI.getSupplierAuditLog(supplier.company);
      setEntries(res.data.results || []);
    } catch (err) {
      console.error('Failed to fetch audit data:', err);
      setError('Failed to load communication audit log');
    } finally {
      setLoading(false);
    }
  }, [supplier?.company]);

  useEffect(() => {
    fetchAuditData();
  }, [fetchAuditData]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left: Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Communication Audit Log
            {!loading && (
              <Badge variant="outline" className="ml-2">
                {entries.length} {entries.length === 1 ? 'event' : 'events'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              <p>{error}</p>
              <button
                onClick={fetchAuditData}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No onboarding activity recorded yet</p>
              <p className="text-sm mt-2">
                Activity will appear here when onboarding workflows, questionnaires, or approval decisions are made for this supplier.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium">When</th>
                    <th className="text-left py-2 px-3 font-medium">Company</th>
                    <th className="text-left py-2 px-3 font-medium">User</th>
                    <th className="text-left py-2 px-3 font-medium">Description</th>
                    <th className="text-left py-2 px-3 font-medium">Attachments</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((log) => {
                    const actionLabel = formatAction(log.action);
                    const cleanDescription = humaniseDescription(log.description || '');
                    const userLabel =
                      log.performed_by_display
                      || log.performed_by_name
                      || (log.performed_by ? log.performed_by_email || '-' : 'System');
                    return (
                      <tr key={log.id} className="border-t">
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {log.performed_at ? formatDate(log.performed_at) : '-'}
                        </td>
                        <td className="py-2 px-3">{log.performed_by_company || 'System'}</td>
                        <td className="py-2 px-3">{userLabel}</td>
                        <td className="py-2 px-3">
                          <span className="font-medium">{actionLabel}</span>
                          {cleanDescription && cleanDescription !== actionLabel && (
                            <span className="text-gray-500"> — {cleanDescription}</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-400">-</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right: Internal Communication Chat */}
      <Card>
        <CardContent className="pt-6">
          <ChatPanel supplier={supplier} />
        </CardContent>
      </Card>
    </div>
  );
}
